import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Loader2, Check } from 'lucide-react';
import { isValidPostalCode } from '@/lib/address';

/**
 * Shared address block used by both quote forms (consumer & business), so the
 * fields and behaviour are identical. Enter postcode + house number and the
 * street + city are auto-filled via the PDOK government address service; the
 * fields stay editable if no match is found.
 */
type AddressFormValues = {
	location_street?: string;
	location_house_number?: string;
	location_postal_code?: string;
	location_city?: string;
};

type LookupStatus = 'idle' | 'loading' | 'found' | 'notfound';

interface AddressFieldsProps {
	/** Whether the address is mandatory to submit the form. Defaults to true (residential offerte-wizard). */
	required?: boolean;
}

export default function AddressFields({ required = true }: AddressFieldsProps) {
	const {
		register,
		setValue,
		watch,
		formState: { errors },
	} = useFormContext<AddressFormValues>();

	const postalCode = watch('location_postal_code');
	const houseNumber = watch('location_house_number');
	const [status, setStatus] = useState<LookupStatus>('idle');
	const lastLookup = useRef<string>('');

	useEffect(() => {
		const pc = (postalCode || '').trim();
		const hn = (houseNumber || '').trim();

		if (!pc || !hn || !isValidPostalCode(pc)) {
			setStatus('idle');

			return;
		}

		const key = `${pc.replace(/\s/g, '').toUpperCase()}|${hn.toUpperCase()}`;
		if (key === lastLookup.current) {
			return;
		}

		const timer = setTimeout(async () => {
			lastLookup.current = key;
			setStatus('loading');

			try {
				const res = await fetch(
					`/api/postcode-lookup?postcode=${encodeURIComponent(pc)}&housenumber=${encodeURIComponent(hn)}`,
				);

				if (!res.ok) {
					setStatus('notfound');

					return;
				}

				const data = await res.json();

				if (data.found && data.street) {
					setValue('location_street', data.street, { shouldValidate: true });
					if (data.city) {
						setValue('location_city', data.city, { shouldValidate: true });
					}
					setStatus('found');
				} else {
					setStatus('notfound');
				}
			} catch {
				setStatus('notfound');
			}
		}, 600);

		return () => clearTimeout(timer);
	}, [postalCode, houseNumber, setValue]);

	const inputClass = (hasError: unknown) =>
		hasError ? 'border-red-500 h-12 text-lg md:text-lg' : 'h-12 text-lg md:text-lg';

	return (
		<div className="space-y-3">
			<Label className="text-lg">
				Adres {required && <span className="text-red-500">*</span>}
				{!required && <span className="text-gray-400 font-normal">(optioneel)</span>}
			</Label>

			{/* Postcode + house number drive the automatic lookup */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<div className="space-y-2">
					<Input
						id="location_postal_code"
						placeholder="Postcode (1234 AB)"
						autoComplete="postal-code"
						className={inputClass(errors.location_postal_code)}
						{...register('location_postal_code')}
					/>
					{errors.location_postal_code && (
						<p className="text-sm text-red-500">{errors.location_postal_code.message as string}</p>
					)}
				</div>
				<div className="space-y-2">
					<Input
						id="location_house_number"
						placeholder="Huisnummer"
						className={inputClass(errors.location_house_number)}
						{...register('location_house_number')}
					/>
					{errors.location_house_number && (
						<p className="text-sm text-red-500">{errors.location_house_number.message as string}</p>
					)}
				</div>
				<div className="flex items-center min-h-[3rem]">
					{status === 'loading' && (
						<span className="text-sm text-gray-500 flex items-center gap-1.5">
							<Loader2 className="size-4 animate-spin" /> Adres ophalen…
						</span>
					)}
					{status === 'found' && (
						<span className="text-sm text-green-600 flex items-center gap-1.5">
							<Check className="size-4" /> Adres gevonden
						</span>
					)}
					{status === 'notfound' && (
						<span className="text-sm text-gray-500">Niet gevonden — vul handmatig in</span>
					)}
				</div>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<div className="space-y-2 md:col-span-2">
					<Input
						id="location_street"
						placeholder="Straat"
						autoComplete="address-line1"
						className={inputClass(errors.location_street)}
						{...register('location_street')}
					/>
					{errors.location_street && <p className="text-sm text-red-500">{errors.location_street.message as string}</p>}
				</div>
				<div className="space-y-2">
					<Input
						id="location_city"
						placeholder="Plaats"
						autoComplete="address-level2"
						className={inputClass(errors.location_city)}
						{...register('location_city')}
					/>
					{errors.location_city && <p className="text-sm text-red-500">{errors.location_city.message as string}</p>}
				</div>
			</div>

			<p className="text-sm text-gray-500">
				Vul postcode + huisnummer in — straat en plaats vullen we automatisch aan.
			</p>
		</div>
	);
}
